import { CaretLeft } from "@phosphor-icons/react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { HashLoader } from "react-spinners";
import useOrderDetails from "../../hooks/useOrderDetails";
import { formatAsMoney } from "../../helpers/numbers";
import { colorVariants, getOrderStatusMeta } from "../../helpers/colors";

export default function OrderDetails() {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const { order, isLoading } = useOrderDetails(orderId!);

  if (isLoading) {
    return (
      <div className="fixed top-0 z-50 left-0 h-screen w-screen flex items-center justify-center bg-black/20 backdrop-blur-sm">
        <HashLoader />
      </div>
    );
  }

  const data = order?.data;
  if (!data) {
    return (
      <div className="p-5 aspect-video flex items-center justify-center">
        Order not found
      </div>
    );
  }

  const statusMeta = getOrderStatusMeta(data.status);

  const variant = colorVariants[statusMeta.color] || colorVariants.gray;

  return (
    <div className="flex flex-col items-start gap-5 px-5 pb-20">
      {/* Header */}
      <div className="flex items-center gap-2 p-4 bg-white">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="p-2 py-3 text-center text-deepBlue border px-6 rounded-xl font-medium flex gap-2 items-center border-gray-300"
        >
          <CaretLeft weight="bold" />
          <span>Back</span>
        </button>
        <h1 className="text-lg font-medium ml-4">Order Details</h1>
      </div>

      <div className="flex-1 overflow-y-auto gap-y-6 flex flex-col w-full">
        {/* Order Info */}
        <div className="bg-white border rounded-xl p-4 space-y-3 w-full">
          <div className="font-bold text-lg">Order #{data.id.slice(0, 8)}</div>
          <div className="opacity-70">
            Placed on {moment(data.created_at).format("MMM D, YYYY h:mm A")}
          </div>
          <div className="flex justify-between items-center">
            <div className="opacity-70">Status</div>
            <div
              className={`px-3 py-1 rounded-md font-medium capitalize bg-${statusMeta.color}-100 border border-${statusMeta.color}-300 text-${statusMeta.color}-600`}
            >
              {statusMeta.label}
            </div>
          </div>
          <div className="flex justify-between items-center">
            <div className="opacity-70">Total</div>
            <div className="font-bold">
              ₦ {formatAsMoney(data.total_amount)}
            </div>
          </div>
        </div>

        {data.status !== "completed" && (
          <div
            className={`flex gap-3 rounded-xl p-4 items-center ${variant.container}`}
          >
            <div className={variant.iconWrapper}>
              <statusMeta.icon size={24} className={variant.icon} />
            </div>
            <div>
              <div className="flex-1 capitalize font-medium">
                {statusMeta.label}
              </div>
              <div>{statusMeta.description}</div>
            </div>
          </div>
        )}

        {/* Shipping Info */}
        <div className="bg-white border rounded-xl p-4 space-y-2">
          <div className="font-bold text-lg">Shipping Address</div>
          <div className="opacity-80">
            {data.shipping_address}, {data.shipping_city}, {data.shipping_state}
            , {data.shipping_country}, {data.shipping_zip_code}
          </div>
        </div>

        {/* Items */}
        <div className="bg-white border rounded-xl p-4 space-y-4">
          <div className="font-bold text-lg">Items</div>
          <div className="divide-y">
            {data.items.map((item, idx) => {
              const statusMeta = getOrderStatusMeta(item.status);
              return (
                <div
                  key={idx}
                  onClick={() =>
                    navigate(`/payments/orders/${data.id}/item/${item.id}`, {
                      state: { item, order: data },
                    })
                  }
                  className="flex gap-4 items-center py-3 cursor-pointer hover:bg-gray-50 p-2 rounded-lg"
                >
                  <img
                    src={item.product?.images?.[0].toString()}
                    alt={item.product.name}
                    className="w-14 h-14 rounded-lg object-cover bg-gray-200"
                  />
                  <div className="flex-1">
                    <div className="font-medium truncate">
                      {item.product.name}
                    </div>
                    <div className="flex flex-col">
                      <div className="font-medium">
                        ₦ {formatAsMoney(parseFloat(item.subtotal))}
                      </div>
                      <div className="text-sm opacity-70">
                        Qty: {item.quantity}
                      </div>
                    </div>
                  </div>
                  <div
                    className={`px-3 py-1 rounded-md font-medium capitalize bg-${statusMeta.color}-100 border border-${statusMeta.color}-300 text-${statusMeta.color}-600 text-center text-sm`}
                  >
                    {statusMeta.label}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
